<template>
  <!-- Standard Layout -->
  <div v-if="!isFullscreenLayout" class="app-container standard-layout">
    <Navbar v-if="!isLoading" />
    <OnboardingBanner v-if="!isLoading" />
    <main class="main-content">
      <LoadingState v-if="isLoading">Loading application...</LoadingState>
      <router-view v-else />
    </main>
    <footer class="app-footer">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col md:flex-row justify-between items-center">
          <p class="text-gray-500 text-sm">
            © {{ new Date().getFullYear() }} Crescendo. All rights reserved.
            <span
              v-if="isStaging"
              class="ml-2 px-2 py-1 bg-yellow-200 text-yellow-800 text-xs rounded font-semibold"
            >
              STAGING
            </span>
          </p>
          <div class="mt-4 md:mt-0">
            <router-link
              to="/about"
              class="text-gray-500 hover:text-primary text-sm mx-2"
              >About</router-link
            >
            <router-link
              to="/terms"
              class="text-gray-500 hover:text-primary text-sm mx-2"
              >Terms</router-link
            >
            <router-link
              to="/privacy"
              class="text-gray-500 hover:text-primary text-sm mx-2"
              >Privacy Policy</router-link
            >
            <router-link
              to="/contact"
              class="text-gray-500 hover:text-primary text-sm mx-2"
              >Contact</router-link
            >
          </div>
        </div>
      </div>
    </footer>
  </div>

  <!-- Fullscreen Layout (for messaging) -->
  <div v-else class="app-container fullscreen-layout">
    <LoadingState v-if="isLoading">Loading application...</LoadingState>
    <router-view v-else />
  </div>

  <!-- Global Post-Lesson Prompt -->
  <GlobalPostLessonPrompt />

  <!-- Cookie consent (renders only when analytics is configured and no choice stored) -->
  <CookieConsentBanner />

  <!-- Debug Panel (development only) -->
  <DebugPanel position="bottom-left" />
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import Navbar from "@/components/NavBar.vue";
import OnboardingBanner from "@/components/OnboardingBanner.vue";
import LoadingState from "@/components/LoadingState.vue";
import GlobalPostLessonPrompt from "@/components/GlobalPostLessonPrompt.vue";
import DebugPanel from "@/components/dev/DebugPanel.vue";
import CookieConsentBanner from "@/components/CookieConsentBanner.vue";
import { initializeAuthListener } from "@/services/auth.service";

export default {
  name: "App",
  components: {
    Navbar,
    OnboardingBanner,
    LoadingState,
    GlobalPostLessonPrompt,
    DebugPanel,
    CookieConsentBanner,
  },
  setup() {
    const route = useRoute();
    const isLoading = ref(true);

    // Determine if current route needs fullscreen layout
    const isFullscreenLayout = computed(() => {
      return route.meta?.layout === "fullscreen";
    });

    // Determine if we're in staging environment
    const isStaging = computed(() => {
      return process.env.VUE_APP_ENVIRONMENT === "staging";
    });

    onMounted(async () => {
      try {
        console.log("App mounted, initializing auth listener");
        // Initialize auth listener
        await initializeAuthListener();
        console.log("Auth listener initialized");

        // Set up viewport height calculation for mobile
        updateViewportHeight();
        window.addEventListener("resize", updateViewportHeight);
        window.addEventListener("orientationchange", () => {
          setTimeout(updateViewportHeight, 100);
        });
      } catch (error) {
        console.error("Error initializing app:", error);
      } finally {
        isLoading.value = false;
      }
    });

    // Update CSS custom property for viewport height
    const updateViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    return {
      isLoading,
      isFullscreenLayout,
      isStaging,
    };
  },
};
</script>

<style>
/* Global layout system styles */

/* App container base */
.app-container {
  width: 100%;
  position: relative;
}

/* Standard layout with navbar and footer */
.standard-layout {
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  display: flex;
  flex-direction: column;
}

/* Navbar - fixed height */
.standard-layout .navbar {
  height: 64px; /* Fixed height instead of vh */
  flex-shrink: 0;
}

/* Main content - grows to fill available space */
.standard-layout .main-content {
  flex: 1;
  min-height: 0; /* Allow flexbox to calculate height */
  overflow-y: auto;
  overflow-x: hidden; /* Prevent horizontal scroll */
}

/* Footer - fixed height, sticks to bottom */
.standard-layout .app-footer {
  height: 64px; /* Fixed height instead of vh */
  background-color: white;
  box-shadow: 0 -1px 3px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: auto; /* Push footer to bottom */
}

/* Fullscreen layout (no navbar/footer) */
.fullscreen-layout {
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Mobile-specific adjustments */
@media (max-width: 767px) {
  .fullscreen-layout {
    /* Use dynamic viewport height for mobile */
    height: 100dvh;
    height: calc(var(--vh, 1vh) * 100);
    /* Ensure proper handling of mobile browser UI */
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
  }

  .standard-layout {
    height: 100dvh;
    height: calc(var(--vh, 1vh) * 100);
  }

  /* Mobile navbar height - keep fixed height */
  .standard-layout .navbar {
    height: 64px;
  }

  /* Mobile main content - let it grow */
  .standard-layout .main-content {
    flex: 1;
    min-height: 0;
  }

  /* Mobile footer height - keep fixed height */
  .standard-layout .app-footer {
    height: 64px;
  }
}

/* iOS Safari specific fixes */
@supports (-webkit-touch-callout: none) {
  .fullscreen-layout {
    height: -webkit-fill-available;
  }

  .standard-layout {
    height: -webkit-fill-available;
  }
}

/* Ensure proper viewport handling */
html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow-x: hidden;
}

#app {
  height: 100%;
}

/* Loading state styling in fullscreen */
.fullscreen-layout .loading-state {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
